import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';

// Android-App (Capacitor): Sie lädt die Web-App vom Server und stellt dabei window.Capacitor bereit. Im Browser fehlt es,
// dann tun die Funktionen hier nichts.
interface CapacitorBridge {
  isNativePlatform?: () => boolean;
  isPluginAvailable?: (name: string) => boolean;
  nativePromise?: (plugin: string, method: string, options?: Record<string, unknown>) => Promise<unknown>;
  addListener?: (plugin: string, event: string, callback: (data: unknown) => void) => { remove(): unknown };
}

function bridge(): CapacitorBridge | undefined {
  return (window as { Capacitor?: CapacitorBridge }).Capacitor;
}

export function isNativeApp(): boolean {
  return bridge()?.isNativePlatform?.() === true;
}

// Einmal beim Start: helle Symbole in der Statusleiste, denn dahinter steht immer das Navy der Kopfzeile (base.css
// html::before). Die Navigationsleiste unten folgt weiter dem Systemthema, passend zum hellen bzw. dunklen Seitenhintergrund.
export function setupNativeApp() {
  const cap = bridge();
  if (!isNativeApp() || !cap?.nativePromise) return;
  cap.nativePromise('SystemBars', 'setStyle', { style: 'DARK', bar: 'StatusBar' }).catch(() => {});
}

// Push-Nachrichten in der App: nach dem Anmelden einmal um Erlaubnis fragen (Android fragt selbst) und das Handy beim
// Server anmelden. Tippt man auf eine Nachricht, öffnet sich die passende Seite. Ältere Apps ohne Push: nichts.
export function useNativePush(userId: string | null) {
  // navigate wechselt mit jeder Seite; die Anmeldung soll nur einmal je Login laufen
  const navigate = useNavigate();
  const go = useRef(navigate);
  go.current = navigate;
  useEffect(() => {
    const cap = bridge();
    const { nativePromise: call, addListener: listen } = cap ?? {};
    if (!userId || !isNativeApp() || !call || !listen || !cap?.isPluginAvailable?.('PushNotifications')) return;
    let gone = false;
    let answered = false;
    let wait: ReturnType<typeof setTimeout> | undefined;
    // Klappt die Anmeldung bei Google nicht, dem Server melden (steht dort im Protokoll), sonst sieht man es nirgends
    const report = (error: unknown) => {
      if (!gone) api.post('/api/push/error', { error: String(error ?? 'unbekannt').slice(0, 2000) }).catch(() => {});
    };
    const subs = [
      listen('PushNotifications', 'registration', (data) => {
        answered = true;
        const token = (data as { value?: unknown } | null)?.value;
        if (!gone && typeof token === 'string') api.put('/api/push/device', { token, platform: 'android' }).catch(() => {});
      }),
      listen('PushNotifications', 'registrationError', (data) => {
        answered = true;
        report((data as { error?: unknown } | null)?.error);
      }),
      listen('PushNotifications', 'pushNotificationActionPerformed', (data) => {
        const url = (data as { notification?: { data?: { url?: unknown } } } | null)?.notification?.data?.url;
        if (typeof url === 'string' && /^\/(app|kanzlei)(\/|$)/.test(url)) go.current(url);
      }),
    ];
    // Bleibt die App irgendwo hängen, steht im Protokoll, bei welchem Schritt. Google braucht für den ersten Gerätecode
    // manchmal über eine halbe Minute (auf einem Samsung Fold so gesehen), daher erst nach 3 Minuten.
    let step = 'checkPermissions';
    (async () => {
      const { enabled } = await api.get<{ enabled: boolean }>('/api/push');
      if (!enabled || gone) return;
      wait = setTimeout(() => answered || report(`Nach 3 Minuten noch keine Antwort (bei ${step})`), 180_000);
      let perm = (await call('PushNotifications', 'checkPermissions')) as { receive?: string };
      if (perm.receive?.startsWith('prompt')) {
        step = 'requestPermissions';
        perm = (await call('PushNotifications', 'requestPermissions')) as { receive?: string };
      }
      if (perm.receive !== 'granted' || gone) {
        answered = true;
        return;
      }
      step = 'register';
      await call('PushNotifications', 'register');
      step = 'Gerätecode von Google';
    })().catch((err: unknown) => report(`${step}: ${err instanceof Error ? err.message : String(err)}`));
    return () => {
      gone = true;
      clearTimeout(wait);
      for (const s of subs) s.remove();
    };
  }, [userId]);
}
